/**
 * Design Memory — store abstraction.
 *
 * `MemoryStore` is the interface the engine depends on; `InMemoryMemoryStore`
 * is the default, DB-free implementation. Memory is partitioned by scope
 * (batch / session / project / tenant / marketplace). A future pgvector or
 * vector-DB store implements the SAME interface, so swapping the backing store
 * changes nothing for consumers — this is the persistence seam.
 */

import { SCOPE_TYPES } from './memoryVocabulary';

export class NotImplementedError extends Error {
  constructor(message) { super(message); this.name = 'NotImplementedError'; }
}

/** Normalize a scope to a stable string key. */
export const scopeKey = (scope = {}) => {
  const type = scope.type || 'session';
  if (!SCOPE_TYPES.includes(type)) throw new Error(`Unknown memory scope type "${type}"`);
  return `${type}:${scope.id || 'default'}`;
};

/** Abstract store. Concrete stores implement all four methods. */
export class MemoryStore {
  // eslint-disable-next-line no-unused-vars
  add(fingerprint, options = {}) { throw new NotImplementedError('MemoryStore.add'); }
  // eslint-disable-next-line no-unused-vars
  query(options = {}) { throw new NotImplementedError('MemoryStore.query'); }
  // eslint-disable-next-line no-unused-vars
  size(options = {}) { throw new NotImplementedError('MemoryStore.size'); }
  // eslint-disable-next-line no-unused-vars
  clear(options = {}) { throw new NotImplementedError('MemoryStore.clear'); }
}

/**
 * In-memory, scope-partitioned store. Fingerprints are deduped by id within a
 * scope; queries return most-recent-first, optionally capped by `limit`.
 */
export class InMemoryMemoryStore extends MemoryStore {
  constructor() {
    super();
    this._scopes = new Map(); // scopeKey → { ids:Set, list:fingerprint[] }
  }

  _bucket(scope) {
    const key = scopeKey(scope);
    if (!this._scopes.has(key)) this._scopes.set(key, { ids: new Set(), list: [] });
    return this._scopes.get(key);
  }

  add(fingerprint, { scope } = {}) {
    if (!fingerprint || !fingerprint.id) throw new Error('add: fingerprint with an id is required');
    const bucket = this._bucket(scope);
    if (bucket.ids.has(fingerprint.id)) return false; // already remembered in this scope
    bucket.ids.add(fingerprint.id);
    bucket.list.push(fingerprint);
    return true;
  }

  query({ scope, limit } = {}) {
    const bucket = this._bucket(scope);
    const recentFirst = [...bucket.list].reverse();
    return typeof limit === 'number' ? recentFirst.slice(0, Math.max(0, limit)) : recentFirst;
  }

  size({ scope } = {}) { return this._bucket(scope).list.length; }

  clear({ scope } = {}) {
    if (scope) this._scopes.delete(scopeKey(scope));
    else this._scopes.clear();
  }
}
